export const CUSTOMER_IMAGE_BETA_QUERY = "customer_image_beta";

export const CUSTOMER_IMAGE_BETA_FIELD_ID = "field-customer-image-beta";

export function enableCustomerImageBeta(publicState) {
  if (!publicState?.template || !isSupportedCustomerImageTemplate(publicState.template)) return publicState;
  const next = structuredClone(publicState);
  const existing = (next.template.fields ?? []).find((field) => field.role === "customerImage");
  const field = existing || {
    id: CUSTOMER_IMAGE_BETA_FIELD_ID,
    label: "Eget bilde",
    type: "file",
    role: "customerImage",
    active: true,
    required: false,
    defaultValue: null,
    options: [],
    priceDeltaNok: 0,
  };
  Object.assign(field, {
    active: true,
    hiddenByDefault: false,
    betaStudio: true,
    acceptMime: ["image/png", "image/jpeg", "image/webp"],
    maxFileBytes: 2_000_000,
    help: "Last opp, beskjær og kontroller bildet før bestilling.",
  });
  if (!existing) next.template.fields = [...(next.template.fields ?? []), field];
  next.template.customerImageBeta = true;
  return next;
}

export function isSupportedCustomerImageTemplate(template = {}) {
  return template.outputLayout !== "zepto-large-sheet"
    && template.outputLayout !== "qr-label-sheet"
    && template.outputLayout !== "wall-decor-sheet"
    && template.outputLayout !== "map-decor-sheet"
    && template.outputLayout !== "flag-decor-sheet"
    && Number(template.widthMm || 0) === 30
    && Number(template.heightMm || 0) === 13;
}

export function customerImageQuality({ width, height, zoom = 1, printWidthMm = 9.7 } = {}) {
  const shortestSide = Math.max(0, Math.min(Number(width) || 0, Number(height) || 0));
  const visiblePixels = shortestSide / Math.max(1, Number(zoom) || 1);
  const ppi = Math.round(visiblePixels / (Math.max(1, Number(printWidthMm) || 9.7) / 25.4));
  if (ppi >= 300) return { status: "ready", ppi, label: "Klar til trykk", message: "Utsnittet har god oppløsning for denne størrelsen." };
  if (ppi >= 180) return { status: "warning", ppi, label: "Kan forbedres", message: "Bildet kan bli litt mindre skarpt i trykk. Prøv å zoome mindre eller velg et større bilde." };
  return { status: "low", ppi, label: "Lav bildekvalitet", message: "Bildet kan bli tydelig uskarpt i trykk. Du kan fortsatt velge å bestille." };
}

export function customerImageDrawLayout({ width, height, zoom = 1, positionX = 0, positionY = 0, outputSize = 512 } = {}) {
  const sourceWidth = Math.max(1, Number(width) || 1);
  const sourceHeight = Math.max(1, Number(height) || 1);
  const size = Math.max(64, Math.round(Number(outputSize) || 512));
  const scale = Math.max(size / sourceWidth, size / sourceHeight) * Math.max(1, Number(zoom) || 1);
  const drawWidth = sourceWidth * scale;
  const drawHeight = sourceHeight * scale;
  const overflowX = Math.max(0, drawWidth - size);
  const overflowY = Math.max(0, drawHeight - size);
  const normalizedX = Math.max(-1, Math.min(1, Number(positionX) || 0));
  const normalizedY = Math.max(-1, Math.min(1, Number(positionY) || 0));
  return {
    size,
    drawWidth,
    drawHeight,
    drawX: -((normalizedX + 1) / 2) * overflowX,
    drawY: -((normalizedY + 1) / 2) * overflowY,
  };
}

export function customerImagePositionAfterDrag({ width, height, zoom = 1, positionX = 0, positionY = 0, deltaX = 0, deltaY = 0, viewportSize = 512 } = {}) {
  const layout = customerImageDrawLayout({ width, height, zoom, positionX, positionY, outputSize: viewportSize });
  const overflowX = Math.max(0, layout.drawWidth - layout.size);
  const overflowY = Math.max(0, layout.drawHeight - layout.size);
  return {
    positionX: overflowX > 0 ? clampUnit(Number(positionX || 0) - (2 * Number(deltaX || 0)) / overflowX) : 0,
    positionY: overflowY > 0 ? clampUnit(Number(positionY || 0) - (2 * Number(deltaY || 0)) / overflowY) : 0,
  };
}

function clampUnit(value) {
  return Math.max(-1, Math.min(1, Number(value) || 0));
}
